if (!customElements.get('brush-exit-popup')) {
  class BrushExitPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.brush-popup__dialog');
      this.overlay = this.querySelector('.brush-popup__overlay');
      this.teaser = this.querySelector('.brush-popup__teaser');
      this.form = this.querySelector('form');
      this.formState = this.querySelector('.brush-popup__state--form');
      this.successState = this.querySelector('.brush-popup__state--success');
      this.sectionId = this.dataset.sectionId;
      this.storeKey = 'naturall-brush-popup';
      this.submitKey = 'naturall-brush-popup-submitted';
      this.dismissDays = parseInt(this.dataset.dismissDays || '7', 10);
      this.delayMs = parseInt(this.dataset.delay || '0', 10) * 1000;
      this.isOpen = false;
      this.armed = false;

      this.querySelectorAll('[data-brush-close]').forEach((el) => el.addEventListener('click', () => this.close(true)));
      this.querySelectorAll('[data-brush-open]').forEach((el) => el.addEventListener('click', () => this.open()));
      this.querySelector('[data-brush-teaser-close]')?.addEventListener('click', () => this.hideTeaser(true));
      this.onKeydown = this.onKeydown.bind(this);
      this.onMouseOut = this.onMouseOut.bind(this);

      if (this.form) {
        this.form.addEventListener('submit', () => {
          try { sessionStorage.setItem(this.submitKey, '1'); } catch (e) {}
        });
      }

      if (this.dataset.designMode !== undefined) {
        document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === this.sectionId) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === this.sectionId) this.close(false); });
      }

      if (new URLSearchParams(window.location.search).get('brush-popup') === 'preview') {
        this.open();
        return;
      }

      if (this.handlePostedForm()) return;
      if (!this.isDesktop()) return;

      const state = this.dataset.designMode !== undefined ? {} : this.getState();
      if (state.subscribed) return;
      if (state.dismissedAt && Date.now() - state.dismissedAt < this.dismissDays * 86400000) {
        if (!state.teaserClosed) this.showTeaser();
        return;
      }

      window.setTimeout(() => {
        this.armed = true;
        document.addEventListener('mouseout', this.onMouseOut);
        document.documentElement.addEventListener('mouseleave', this.onMouseOut);
      }, this.delayMs);
    }

    disconnectedCallback() {
      document.removeEventListener('mouseout', this.onMouseOut);
      document.removeEventListener('keydown', this.onKeydown);
    }

    isDesktop() {
      return window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches;
    }

    handlePostedForm() {
      let submitted = false;
      try { submitted = sessionStorage.getItem(this.submitKey) === '1'; } catch (e) {}
      if (!submitted && window.location.hash !== '#BrushExitPopup') return false;
      try { sessionStorage.removeItem(this.submitKey); } catch (e) {}

      const posted = this.formState?.dataset.posted === 'true' || /customer_posted=true/.test(window.location.search);
      const hasError = !!this.querySelector('[data-brush-error]');
      if (posted && !hasError) {
        this.setState({ subscribed: true });
        this.formState.hidden = true;
        this.successState.hidden = false;
        this.open();
        return true;
      }
      if (hasError) {
        this.open();
        return true;
      }
      return false;
    }

    onMouseOut(event) {
      if (!this.armed || this.isOpen) return;
      // In a preview frame (Canvas/theme editor) leaving the frame on any edge counts; on the storefront, the top edge.
      const inFrame = window.self !== window.top;
      if (inFrame) {
        if (event.relatedTarget) return;
      } else {
        if (event.relatedTarget && event.relatedTarget.nodeName !== 'HTML') return;
        if (event.clientY > 60) return;
      }
      document.removeEventListener('mouseout', this.onMouseOut);
      document.documentElement.removeEventListener('mouseleave', this.onMouseOut);
      // One popup per session: skip if the sale video popup or article guide signup already showed.
      if (document.documentElement.dataset.guideSignupActive === '1') return;
      try { if (sessionStorage.getItem('naturall-popup-shown') === '1') return; } catch (e) {}
      this.open();
      try { sessionStorage.setItem('naturall-popup-shown', '1'); } catch (e) {}
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.hideTeaser(false);
      this.overlay.hidden = false;
      this.dialog.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      const focusTarget = this.successState.hidden ? this.dialog.querySelector('input[type="email"]') : this.successState.querySelector('button');
      (focusTarget || this.dialog).focus();
    }

    close(remember) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.overlay.hidden = true;
      this.dialog.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);
      if (remember && this.dataset.designMode === undefined) {
        const state = this.getState();
        if (!state.subscribed) {
          this.setState({ dismissedAt: Date.now() });
          if (!state.teaserClosed) this.showTeaser();
        }
      }
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    showTeaser() {
      if (!this.teaser || this.dataset.showTeaser !== 'true' || !this.isDesktop()) return;
      this.teaser.hidden = false;
    }

    hideTeaser(remember) {
      if (!this.teaser) return;
      this.teaser.hidden = true;
      if (remember) this.setState({ teaserClosed: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getState() {
      try { return JSON.parse(localStorage.getItem(this.storeKey)) || {}; } catch (e) { return {}; }
    }

    setState(patch) {
      try { localStorage.setItem(this.storeKey, JSON.stringify(Object.assign(this.getState(), patch))); } catch (e) {}
    }
  }

  customElements.define('brush-exit-popup', BrushExitPopup);
}
